import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Award, Compass, Users, Brain, CheckSquare, Clock, FileText, Lightbulb, Map, AlertCircle, Printer } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { getResults, clearResults } from '../lib/testResults';

const TEST_META_BTECH = [
  { id: 'btech-t1', n: 1, title: 'Interest Map', icon: Compass, desc: 'Engineering interest direction across 10 areas' },
  { id: 'btech-t2', n: 2, title: 'Branch Fit Deep Dive', icon: Users, desc: 'Best-fit B.Tech branch for your interest' },
  { id: 'btech-t3', n: 3, title: 'AI-Ready Skills Audit', icon: Brain, desc: 'AI-readiness across every branch' },
  { id: 'btech-t4', n: 4, title: 'Core Engineering Future Pathways', icon: CheckSquare, desc: 'EV, robotics, smart infra, biotech futures' },
];
const TEST_META_MGMT = [
  { id: 'management-t1', n: 1, title: 'Business Interest Map', icon: Compass, desc: 'Business interest direction across 10 areas' },
  { id: 'management-t2', n: 2, title: 'Specialization Fit Deep Dive', icon: Users, desc: 'Best-fit BBA/MBA specialization' },
  { id: 'management-t3', n: 3, title: 'AI-Ready Business Skills', icon: Brain, desc: 'AI-readiness for business and management' },
  { id: 'management-t4', n: 4, title: 'Business & Management Future Pathways', icon: CheckSquare, desc: 'Future-ready business careers' },
];

const BTECH_RECOMMENDATIONS = {
  CSE_IT: { stream: 'CSE / IT', whyFit: 'You enjoy building things on screen and step-by-step logic — perfect for coding-heavy software roles.', skills: ['Python or JavaScript basics', 'Data structures', 'Build a portfolio of 3 small projects', 'Practice problem-solving on platforms like HackerRank'], roles: ['Frontend Developer', 'Backend Developer', 'Full Stack Engineer', 'Mobile App Developer'] },
  AI_DS: { stream: 'AI / Data Science', whyFit: 'You enjoy numbers, patterns, and learning from data — great fit for AI/ML and data roles.', skills: ['Python + Pandas/NumPy', 'Statistics basics', 'One ML project using scikit-learn', 'SQL for data querying'], roles: ['Data Analyst', 'ML Engineer', 'Data Scientist', 'AI Product Analyst'] },
  CYBER: { stream: 'Cyber Security', whyFit: 'You like spotting risks and protecting systems — well-suited for security careers.', skills: ['Networking fundamentals', 'Linux basics', 'CTF challenges on TryHackMe / HackTheBox', 'Cyber-security certifications path'], roles: ['Security Analyst', 'Penetration Tester', 'SOC Analyst', 'Cloud Security Engineer'] },
  CLOUD_IOT: { stream: 'Cloud / IoT', whyFit: 'You are curious about how big apps run and how devices connect — fits cloud & IoT systems.', skills: ['Linux / shell', 'One cloud provider (AWS or Azure) basics', 'A small IoT or Docker project', 'Networking basics'], roles: ['Cloud Engineer', 'DevOps Engineer', 'IoT Developer', 'Site Reliability Engineer'] },
  ECE: { stream: 'ECE (Electronics & Communication)', whyFit: 'You like circuits and how signals are sent — strong fit for ECE specializations.', skills: ['Basic electronics (resistors, capacitors)', 'Arduino / microcontroller project', 'Signal processing basics', 'Embedded C'], roles: ['Embedded Engineer', 'VLSI Engineer', 'Telecom Engineer', 'Hardware Designer'] },
  EEE: { stream: 'EEE (Electrical & Electronics)', whyFit: 'You are interested in power, motors, and electrical systems.', skills: ['Circuit theory', 'Power systems basics', 'MATLAB for simulation', 'PLC / automation basics'], roles: ['Power Systems Engineer', 'Control Engineer', 'Renewable Energy Engineer', 'Industrial Automation Engineer'] },
  MECH: { stream: 'Mechanical Engineering', whyFit: 'You enjoy machines and hands-on building / repairing.', skills: ['CAD (AutoCAD / SolidWorks)', 'Thermodynamics basics', 'A hands-on workshop project', 'Manufacturing process basics'], roles: ['Design Engineer', 'Manufacturing Engineer', 'Automotive Engineer', 'HVAC Engineer'] },
  ROBOTICS: { stream: 'Mechatronics / Robotics', whyFit: 'You like machines + electronics + automation — a robotics fit.', skills: ['Arduino / Raspberry Pi project', 'Basic mechanics + electronics', 'ROS (Robot Operating System) intro', 'Python for automation'], roles: ['Robotics Engineer', 'Automation Engineer', 'Mechatronics Designer', 'Drone Engineer'] },
  CIVIL: { stream: 'Civil Engineering', whyFit: 'You are interested in buildings, roads, and construction.', skills: ['AutoCAD basics', 'Structural drawing reading', 'A site visit / case study', 'Surveying basics'], roles: ['Site Engineer', 'Structural Engineer', 'Project Manager (Construction)', 'Urban Planner'] },
  CHEM_BIO: { stream: 'Chemical / Biotechnology', whyFit: 'You are interested in lab work, chemistry, or biology.', skills: ['Lab safety and basic techniques', 'A documented lab experiment', 'One biology / chemistry project', 'Read recent industry articles'], roles: ['Lab Scientist', 'Pharma R&D', 'Biotech Researcher', 'Quality Analyst'] },
};

const MGMT_RECOMMENDATIONS = {
  MKT: { stream: 'Marketing', whyFit: 'You enjoy convincing people, creating campaigns, and understanding buyers.', skills: ['Social media campaign basics', 'Copywriting practice', 'Run a small Instagram / college campaign', 'Google Analytics intro'], roles: ['Brand Manager', 'Digital Marketer', 'Content Strategist', 'Product Marketing Manager'] },
  FIN: { stream: 'Finance', whyFit: 'You enjoy numbers, budgets, and profit/loss — fits finance career path.', skills: ['Excel + basic accounting', 'Read a simple balance sheet', 'Stock market basics (paper trade)', 'Financial modeling intro'], roles: ['Financial Analyst', 'Investment Banker (entry)', 'Equity Researcher', 'Credit Analyst'] },
  HR: { stream: 'Human Resources (HR)', whyFit: 'You enjoy understanding people, resolving conflicts, and team-building.', skills: ['Communication and active listening', 'Basic labour-law awareness', 'Lead a small team project', 'Interview practice'], roles: ['HR Generalist', 'Talent Acquisition', 'L&D Specialist', 'HR Business Partner'] },
  OPS: { stream: 'Operations', whyFit: 'You enjoy process design and making things run smoothly.', skills: ['Excel + process flowcharts', 'Lean / Six Sigma intro', 'A small process-improvement case study', 'Supply chain basics'], roles: ['Operations Analyst', 'Supply Chain Coordinator', 'Process Manager', 'Logistics Manager'] },
  ANALYTICS: { stream: 'Business Analytics', whyFit: 'You enjoy data + business decisions — strong analytics fit.', skills: ['Excel advanced + SQL basics', 'Power BI / Tableau intro', 'A small dataset analysis project', 'Statistics fundamentals'], roles: ['Business Analyst', 'Data Analyst', 'BI Developer', 'Strategy Analyst'] },
  PM_GM: { stream: 'Product / General Management', whyFit: 'You enjoy leading, planning, and decision-making.', skills: ['Product thinking basics', 'Lead a club / fest', 'Read product case studies', 'Public speaking practice'], roles: ['Product Manager (associate)', 'Operations Manager', 'Strategy Associate', 'Founder\'s Office Analyst'] },
  ENT: { stream: 'Entrepreneurship', whyFit: 'You enjoy building new things and taking calculated risks.', skills: ['Idea validation interviews', 'Build a small MVP', 'Read startup case studies', 'Pitching practice'], roles: ['Founder', 'Co-founder', 'Startup Operations', 'Venture Analyst'] },
};

const DecisionReport = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(() => getResults());
  const printedOn = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });

  useEffect(() => { setData(getResults()); }, []);

  const isBtech = data.pathway === 'btech';
  const isMgmtNew = data.pathway === 'management' && !!data.tests?.['management-t1'];
  // QuizEngine writes under keys "{pathway}-t1..t4".
  const btechT1 = data.tests?.['btech-t1'];
  const mgmtT1New = data.tests?.['management-t1'];
  const test1 = isBtech ? btechT1 : mgmtT1New;
  const completedCount = Object.keys(data.tests || {}).length;
  const pathway = data.pathway;

  // For B.Tech: map 10 T1 primary tags to legacy recommendation library keys.
  const BTECH_TAG_TO_LEGACY = { CSE_AI_DATA: 'CSE_IT', ECE_ROBOTICS_IOT: 'ECE', MECH_AUTO_MANUFACTURING: 'MECH', CIVIL_INFRA_ENV: 'CIVIL', EEE_ENERGY_EV: 'EEE', BIOTECH_HEALTH_FOOD: 'CHEM_BIO', CHEM_MATERIALS_PROCESS: 'CHEM_BIO', DESIGN_PRODUCT_UX: 'CSE_IT', BUSINESS_TECH_MANAGEMENT: 'CSE_IT', EXPLORER_GENERAL: 'CSE_IT' };
  const btechRec = btechT1 ? BTECH_RECOMMENDATIONS[BTECH_TAG_TO_LEGACY[btechT1.primaryTag] || 'CSE_IT'] : null;

  // For Management (BBA/MBA): map 10 T1 primary tags to recommendation library keys.
  const MGMT_TAG_TO_LEGACY = { MARKETING_BRAND_DIGITAL: 'MKT', FINANCE_ACCOUNTING_INVESTMENT: 'FIN', HR_PEOPLE_LEADERSHIP: 'HR', OPERATIONS_SUPPLY_CHAIN: 'OPS', ENTREPRENEURSHIP_PRODUCT: 'ENT', BUSINESS_ANALYTICS_AI_DATA: 'ANALYTICS', INTERNATIONAL_BUSINESS_ECONOMICS: 'PM_GM', RETAIL_HOSPITALITY_SERVICES: 'MKT', PUBLIC_POLICY_SOCIAL_IMPACT: 'PM_GM', EXPLORER_GENERAL: 'PM_GM' };
  const mgmtNewRec = mgmtT1New ? MGMT_RECOMMENDATIONS[MGMT_TAG_TO_LEGACY[mgmtT1New.primaryTag] || 'MKT'] : null;

  const reset = () => { clearResults(); setData(getResults()); };

  // No results yet — show empty state
  if (!test1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex flex-col items-center justify-center px-6" data-testid="report-empty">
        <FileText size={48} className="text-gray-300 mb-4" />
        <h1 className="text-2xl font-bold text-gray-900 mb-2">No Report Yet</h1>
        <p className="text-gray-600 text-center mb-6 max-w-md">Complete at least Test 1 (Interest Map) and your personalized Decision Report will appear here, ready to download as PDF.</p>
        <Button asChild data-testid="start-tests-btn"><Link to="/pathways/after-10th#tests">Start the 4 Tests</Link></Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="decision-report-page">
      {/* Print styles + screen layout */}
      <style>{`
        @media print {
          @page { size: A4; margin: 18mm 14mm; }
          body { background: white !important; }
          .no-print { display: none !important; }
          .print-card { box-shadow: none !important; border: 1px solid #e5e7eb !important; break-inside: avoid; }
          .print-page-break { page-break-before: always; }
        }
      `}</style>

      {/* Top action bar — hidden when printing */}
      <div className="no-print bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-20">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900" data-testid="back-btn">
          <ArrowLeft size={16} /> Back
        </button>
        <div className="flex gap-2">
          <Button data-testid="reset-report-btn" onClick={reset} size="sm" variant="outline" className="text-xs">Reset</Button>
          <Button data-testid="download-pdf-btn" onClick={() => window.print()} size="sm" className="bg-teal-600 hover:bg-teal-700 text-white">
            <Download size={14} className="mr-1.5" /> Download as PDF
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 space-y-5">
        {/* Header / cover */}
        <div className="print-card bg-white rounded-2xl p-6 md:p-8 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Award className="text-teal-600" size={22} />
            <p className="text-xs font-bold text-teal-600 tracking-wider uppercase">LearnixTa — Career Counselling</p>
          </div>
          <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-2">Your Decision Report</h1>
          <p className="text-sm text-gray-500">After 10th / Inter — {pathway === 'btech' ? 'B.Tech' : 'Management (BBA/MBA)'} pathway</p>
          <p className="text-xs text-gray-400 mt-1">Generated on {printedOn} · {completedCount} of 4 tests completed</p>
        </div>

        {/* Top match — the headline */}
        {isBtech && btechT1 ? (
          <div className="print-card bg-gradient-to-br from-teal-50 to-blue-50 rounded-2xl p-6 md:p-8 border border-teal-200">
            <p className="text-xs font-bold text-teal-700 mb-2 tracking-wider uppercase">Top Match (Test 1)</p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{btechT1.primaryTitle}</h2>
            {btechT1.secondaryTitle && <p className="text-xs text-gray-600 mb-3"><span className="font-semibold">Secondary direction: </span>{btechT1.secondaryTitle}</p>}
            {btechRec && <p className="text-sm text-gray-700 leading-relaxed">{btechRec.whyFit}</p>}
          </div>
        ) : isMgmtNew && mgmtT1New ? (
          <div className="print-card bg-gradient-to-br from-teal-50 to-blue-50 rounded-2xl p-6 md:p-8 border border-teal-200">
            <p className="text-xs font-bold text-teal-700 mb-2 tracking-wider uppercase">Top Match (Test 1)</p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{mgmtT1New.primaryTitle}</h2>
            {mgmtT1New.secondaryTitle && <p className="text-xs text-gray-600 mb-3"><span className="font-semibold">Secondary direction: </span>{mgmtT1New.secondaryTitle}</p>}
            {mgmtNewRec && <p className="text-sm text-gray-700 leading-relaxed">{mgmtNewRec.whyFit}</p>}
          </div>
        ) : null}

        {/* Section: Test 1 results detail */}
        <div className="print-card bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Compass className="text-teal-600" size={18} />
            <h3 className="text-base font-bold text-gray-900">Test 1: {isBtech ? 'Interest Map' : 'Business Interest Map'} — Detailed Results</h3>
          </div>
          {((isBtech && btechT1?.tagTotals) || (isMgmtNew && mgmtT1New?.tagTotals)) && (
            <>
              <p className="text-xs text-gray-500 mb-3">Score breakdown across 10 {isBtech ? 'engineering' : 'business'} directions:</p>
              <ol className="space-y-2 mb-2">
                {Object.entries((isBtech ? btechT1 : mgmtT1New).tagTotals).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([tag, score], i) => (
                  <li key={tag} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg">
                    <span className="w-7 h-7 bg-teal-600 text-white rounded-full flex items-center justify-center text-xs font-bold">{i + 1}</span>
                    <span className="flex-1 text-sm font-semibold text-gray-800">{tag.replace(/_/g, ' ')}</span>
                    <span className="text-xs text-gray-500 font-mono">{score} pts</span>
                  </li>
                ))}
              </ol>
            </>
          )}
        </div>

        {/* Additional Test Results (T2, T3, T4) — supports both pathways */}
        {(() => {
          const prefix = isBtech ? 'btech' : 'management';
          const metaList = isBtech ? TEST_META_BTECH : TEST_META_MGMT;
          const additionalKeys = [`${prefix}-t2`, `${prefix}-t3`, `${prefix}-t4`];
          if (!additionalKeys.some(k => data.tests?.[k])) return null;
          return (
            <div className="print-card bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-base font-bold text-gray-900 mb-3">Additional Test Results</h3>
              <div className="space-y-3">
                {additionalKeys.map(k => {
                  const r = data.tests?.[k];
                  if (!r) return null;
                  const meta = metaList.find(m => m.id === k);
                  return (
                    <div key={k} className="p-3 bg-gradient-to-br from-teal-50/40 to-white rounded-lg border border-teal-100">
                      <p className="text-[10px] font-bold text-teal-600 uppercase tracking-wider mb-1">Test {meta?.n} — {meta?.title}</p>
                      <p className="text-sm font-bold text-gray-900">{r.primaryTitle}</p>
                      {r.secondaryTitle && <p className="text-xs text-gray-600">Secondary: {r.secondaryTitle}</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Skills + roadmap */}
        {(() => {
          const rec = isBtech ? btechRec : mgmtNewRec;
          if (!rec) return null;
          return (
            <div className="print-card bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Map className="text-teal-600" size={18} />
                <h3 className="text-base font-bold text-gray-900">Recommended Roadmap — First 4 Skills</h3>
              </div>
              <ul className="space-y-2 mb-5">
                {rec.skills.map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-teal-100 text-teal-700 text-xs flex items-center justify-center font-bold mt-0.5">{i + 1}</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb className="text-blue-600" size={16} />
                <h4 className="text-sm font-bold text-gray-900">Sample Roles to Target</h4>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {rec.roles.map(r => <Badge key={r} className="bg-blue-50 text-blue-700 border border-blue-200">{r}</Badge>)}
              </div>
            </div>
          );
        })()}

        {/* 4-test progress overview */}
        <div className="print-card bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="text-gray-600" size={18} />
            <h3 className="text-base font-bold text-gray-900">Your 4-Test Journey</h3>
          </div>
          <div className="space-y-2">
            {(isBtech ? TEST_META_BTECH : TEST_META_MGMT).map(t => {
              const done = !!data.tests?.[t.id];
              const Icon = t.icon;
              return (
                <div key={t.id} className={`flex items-center gap-3 p-3 rounded-lg border ${done ? 'bg-teal-50 border-teal-200' : 'bg-gray-50 border-gray-200'}`}>
                  <Icon size={18} className={done ? 'text-teal-600' : 'text-gray-400'} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold ${done ? 'text-teal-900' : 'text-gray-700'}`}>Test {t.n}: {t.title}</p>
                    <p className="text-xs text-gray-500">{t.desc}</p>
                  </div>
                  {done ? (
                    <Badge className="bg-teal-600 text-white text-[10px]">Completed</Badge>
                  ) : (
                    <Badge className="bg-gray-100 text-gray-500 text-[10px]">Pending</Badge>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Explore specializations — only shown after Test 1 is done */}
        <div className="print-card bg-gradient-to-br from-teal-50 to-blue-50 border border-teal-200 rounded-2xl p-5">
          <p className="text-xs font-bold text-teal-700 uppercase tracking-wider mb-1">Next Step</p>
          <h3 className="text-base font-bold text-gray-900 mb-1.5">Explore Inner Specializations</h3>
          <p className="text-xs text-gray-700 mb-3 leading-relaxed">Pick your degree (B.Tech / BBA / MBA), then your branch. Inside every inner specialization: a Self-Check Quiz, learning path, projects, careers, industries hiring, and a real industry scenario.</p>
          <Button asChild size="sm" data-testid="explore-specializations-btn" className="bg-teal-600 hover:bg-teal-700 text-white">
            <Link to="/previews">Browse Specializations →</Link>
          </Button>
        </div>

        {/* Disclaimer + next steps */}
        <div className="print-card bg-amber-50 border border-amber-200 rounded-2xl p-5">
          <div className="flex gap-2 mb-2">
            <AlertCircle className="text-amber-600 flex-shrink-0" size={16} />
            <p className="text-xs font-bold text-amber-900">Important Note for Students &amp; Parents</p>
          </div>
          <p className="text-xs text-amber-800 leading-relaxed">
            This Decision Report is a <strong>guide based on your responses</strong>, not a verdict. LearnixTa can make mistakes — reverify outside too. Tests 2–4 will further refine the recommendation. Use this with school counsellors and parents to discuss the right path — not as the only deciding factor.
          </p>
        </div>

        {/* Action buttons (hidden in print) */}
        <div className="no-print flex flex-col sm:flex-row gap-3 pt-2">
          <Button asChild className="flex-1 btn-primary" data-testid="continue-tests-btn">
            <Link to="/pathways/after-10th#tests">Continue Remaining Tests</Link>
          </Button>
          <Button onClick={() => window.print()} variant="outline" className="flex-1" data-testid="print-btn">
            <Printer size={14} className="mr-1.5" /> Print Report
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DecisionReport;
